import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import * as PR from "../../prime-modules/index";
import { deleteUserEsim, esimsICCID, getEsimCapabilities, refreshEsim, resetEsim, restoreUserEsim } from "../../services/api.jsx";
import { useDispatch, useSelector } from "react-redux";
import "./EsimDetails.scss";
import AdminFooter from "../layout/admin-footer";
import AdminHeader from "../layout/admin-header";
import { esimIcon, bundleIcon } from "../../assets/images";
import { formatDateTime, checkDataSize } from "../../utils/reuse";
import { appName } from "../../GlobalConfig.jsx";
import PriceDisplay from "../../components/PriceDisplay";
import { formatUKPhoneNumber } from "../../utils";

const EsimDetails = () => {
  const { searchTerm } = useParams(); // Extract searchTerm from the URL
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const toast = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminData = useSelector((state) => state.adminAuth.adminSessionData);
  const headers = useMemo(() => {
    return { sessionid: adminData.sessionId };
  }, [adminData.sessionId]);
  const [esimData, setEsimData] = useState(location.state?.esimdata)
  const bundlesData = esimData?.activeBundlesData ? esimData?.activeBundlesData : []
  const [createLoading, setCreateLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [isResetCapable, setIsResetCapable] = useState(false);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [iccidValue, setIccidValue] = useState("");
  const [esimActionDialog, setEsimActionDialog] = useState(false);
  const [esimActionLoading, setEsimActionLoading] = useState(false);

  const home = { icon: 'pi pi-home', url: '/' }

  const items = useMemo(() => {
    let items = [
      {
        label: esimData?.userId ? 'Customer details' : 'Customers',
        command: () => {
          esimData?.userId
            ? navigate('/customer-details/' + esimData?.userId)
            : navigate('/users');
        },
      },
    ]
    if (orderId) {
      items.push({
        label: 'Order details',
        command: () => {
          navigate('/orders/details/' + orderId);
        }
      });
    }
    return items;
  }, [esimData?.userId, orderId, navigate]);

  const fetchEsimDetails = useCallback((iccid) => {
    setCreateLoading(true);
    const getResponse = (response) => {
      setCreateLoading(false);
      if (response.result === "SUCCESS") {
        setEsimData(response.data);
      } else {
        const error = response.error || {};
        toast.current?.show({
          severity: error.severity || "error",
          summary: "Error",
          detail: error.errorMsg || error.summary || "An unknown error occurred.",
        });
      }
    };
    esimsICCID(iccid, headers, dispatch, getResponse);
  }, [headers, dispatch]);

  const fetchCapabilities = useCallback((iccid) => {
    if (!iccid) return;
    setCapabilitiesLoading(true);
    getEsimCapabilities(iccid, headers, dispatch, (response) => {
      setCapabilitiesLoading(false);
      if (response.result === "SUCCESS") {
        const data = response.data;
        const canReset = data?.canResetNetworkConnection ?? data?.capabilities?.canResetNetworkConnection ?? false;
        setIsResetCapable(Boolean(canReset));
      } else {
        setIsResetCapable(false);
      }
    });
  }, [headers, dispatch]);

  useEffect(() => {
    const iccidToUse = searchTerm || esimData?.iccid;
    if (iccidToUse) {
      if (!esimData) {
        fetchEsimDetails(iccidToUse);
      }
      fetchCapabilities(iccidToUse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const createdTime = (rowData) => {
    return formatDateTime(rowData?.createdTs || "-")
  }

  const expiryOn = (rowData) => {
    return formatDateTime(rowData?.bundleEndTime || "-")
  }

  const bundleDetails = (rowData) => {
    return (
      <div className="expiry-column">
        <p>{`${rowData?.country?.split("(")[0]} ${rowData?.country ? "|" : ""
          } ${rowData?.isUnlimited
            ? "Unlimited"
            : `${checkDataSize(rowData?.remainingData)}  / ${checkDataSize(
              rowData?.totalData
            )} ${"left"}`
          } `}</p>
      </div>
    )
  }

  const filteredBundles = checked
    ? bundlesData.filter((bundle) => bundle.state === "expired")
    : bundlesData;

  const provider = () => {
    const providerMap = {
      1: 'eSIM-Go',
      2: 'Airalo',
      3: 'Cobira',
      4: 'Uk Domestic',
    };

    return providerMap[esimData?.providerCode] || 'N/A';
  };

  const price = (rowData) => {
    return <PriceDisplay rowData={rowData} />;
  };

  const copyToClipboard = (text, label = "ICCID") => {
    navigator.clipboard.writeText(text).then(() => {
      toast.current?.show({ severity: 'success', summary: 'Success', detail: `${label} has been copied to clipboard.` });
    }).catch(err => {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: `Failed to copy ${label}.` });
    });
  };

  const handleResetEsim = () => {
    const iccidToReset = searchTerm || esimData?.iccid;
    setResetLoading(true);
    resetEsim(iccidToReset, headers, dispatch, (response) => {
      setResetLoading(false);
      if (response.result === "SUCCESS") {
        toast.current?.show({
          severity: "success",
          summary: "eSIM Reset",
          detail: response.data?.message || "The eSIM data has been refreshed successfully.",
        });
        fetchEsimDetails(iccidToReset);
      } else {
        const error = response.error || {};
        toast.current?.show({
          severity: error.severity || "error",
          summary: "Error",
          detail: error.errorMsg || error.summary || "Failed to reset eSIM.",
        });
      }
    });
  };
  const handleRefreshMSISDN = () => {
    const iccidToReset = searchTerm || esimData?.iccid;
    setRefreshLoading(true);
    refreshEsim(iccidToReset, headers, dispatch, (response) => {
      setRefreshLoading(false);
      if (response.result === "SUCCESS") {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.data?.message || "The MSISDN has been refreshed successfully.",
        });
        fetchEsimDetails(iccidToReset);
      } else {
        const error = response.error || {};
        toast.current?.show({
          severity: error.severity || "error",
          summary: "Error",
          detail: error.errorMsg || error.summary || "Failed to refresh MSISDN.",
        });
      }
    });
  };

  const esimUserId = esimData?.userId;
  const isEsimDeleted = Boolean(esimData?.isDeleted ?? esimData?.deleted);

  const handleEsimAction = () => {
    const iccid = esimData?.iccid || searchTerm;
    if (!esimUserId || !iccid) {
      toast.current?.show({
        severity: "error",
        summary: "Unable to update eSIM",
        detail: "The customer associated with this eSIM could not be identified.",
      });
      return;
    }

    const restoring = isEsimDeleted;
    const request = restoring ? restoreUserEsim : deleteUserEsim;
    setEsimActionLoading(true);
    request(esimUserId, iccid, headers, dispatch, (response) => {
      setEsimActionLoading(false);
      if (response.result === "SUCCESS") {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: restoring ? "eSIM restored successfully." : "eSIM deleted successfully.",
        });
        setEsimActionDialog(false);
        fetchEsimDetails(iccid);
        return;
      }

      const error = response.error || {};
      toast.current?.show({
        severity: error.severity || "error",
        summary: "Error",
        detail: error.errorMsg || error.summary || "Unable to update the eSIM. Please try again.",
      });
    });
  };

  const handleSearch = () => {
    if (!iccidValue.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please enter a valid ICCID.",
      });
      return;
    }

    setCreateLoading(true);
    const getResponse = (response) => {
      setCreateLoading(false);
      if (response.result === "SUCCESS") {
        setEsimData(response.data)
        navigate(`/esim-details/${iccidValue}`, { state: { esimdata: response.data } });
        fetchCapabilities(iccidValue);
      } else {
        setEsimData({})
        navigate(`/esim-details/${iccidValue}`);

      }
    };
    esimsICCID(iccidValue, headers, dispatch, getResponse);

  };

  const renderHeader = () => {
    return (
      <div className="flex align-items-center justify-content-between mobile-flex">
        <div className="right flex align-items-center ">
          <h2>eSIM Details</h2>
          <div className="breadcrumb-section">
            <PR.BreadCrumb model={items} home={home} />
          </div>
        </div>
        <span className="p-input-icon-right esim-search-field">
          <i className="pi pi-search" onClick={handleSearch} />
          <PR.InputText placeholder="Search by ICCID" value={iccidValue}
            onChange={(e) => {
              const numericValue = e.target.value.replace(/[^0-9]/g, "");
              setIccidValue(numericValue)
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !createLoading) {
                handleSearch();
              }
            }}
            disabled={createLoading}
          />
        </span>
      </div>
    );
  };

  const esimActivationLink = (platform) => {
    const esimActivationUrl = `https://esimsetup.${platform}.com/esim_qrcode_provisioning?carddata=LPA:1$${esimData?.smdpAddress}$${esimData?.matchingId}`;

    navigator.clipboard.writeText(esimActivationUrl).then(() => {
      toast.current?.show({ severity: 'success', summary: 'Success', detail: `Activation link for ${platform === 'android' ? 'Android' : 'iOS'} has been copied to clipboard.` });
    }).catch(err => {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Failed to copy the activation link.' });
    });
  }
  return (
    <>
      <div className="main">
        <div className="layout-sidebar">
          <AdminHeader />
        </div>
        <div className="layout-content-wrapper">
          <div className="esim-details-section">
            {renderHeader()}
            {createLoading && !esimData?.iccid ? (
              <div className="col-12 text-center admin-userlist-section">
                <div className="progress-spinner">
                  <PR.ProgressSpinner
                    className="progressSpinner"
                    animationDuration=".5s"
                  />
                </div>
              </div>
            ) : !esimData?.iccid ? (
              <div className="users-data-table text-center p-5">
                <span className="text-gray-500 font-semibold text-lg">No eSIM details found</span>
              </div>
            ) : (<div className="users-data-table">
              <div className="iccid-section">
                <div className="flex align-items-center gap-2 m-flex">
                  <h4 className={esimData?.isDeleted ? "deleted-iccid" : ""}>
                    ICCID - <span>{searchTerm}</span>
                    <PR.Button icon="pi pi-copy" className="p-button-text copy-btn" onClick={() => copyToClipboard(searchTerm)} />
                  </h4>
                  {esimData?.subscriptionStatus &&
                    <>
                      <div className="divider"></div>
                      <h4>
                        MSISDN - <span>{esimData.msisdn ? formatUKPhoneNumber(esimData.msisdn) : '-'}</span>
                        <PR.Button icon="pi pi-copy" className="p-button-text copy-btn" onClick={() => copyToClipboard(esimData.msisdn, "MSISDN")} />
                        <PR.Button
                          icon={refreshLoading ? "pi pi-spin pi-spinner" : "pi pi-refresh"}
                          className="refresh-msisdn-btn"
                          onClick={handleRefreshMSISDN}
                          disabled={refreshLoading || createLoading}
                          tooltip="Refresh MSISDN"
                          tooltipOptions={{ position: 'top' }}
                        />
                      </h4> </>}
                  {
                    appName !== 'data2go-iot' && (
                      <>
                        {esimData?.smdpAddress && esimData?.matchingId &&
                          <>
                            <div className="divider"></div>
                            <h4>iOS Activation link
                              <PR.Button icon="pi pi-copy" className="p-button-text copy-btn" onClick={esimActivationLink.bind(null, 'apple')} />
                            </h4>
                          </>
                        }
                        {esimData?.smdpAddress && esimData?.matchingId &&
                          <>
                            <div className="divider"></div>
                            <h4>Android Activation link
                              <PR.Button icon="pi pi-copy" className="p-button-text copy-btn" onClick={esimActivationLink.bind(null, 'android')} />
                            </h4>
                          </>
                        }
                      </>
                    )
                  }
                </div>
                <div className="status-section">
                  <p>eSIM Status: <span className={`status-badge ${esimData?.isDeleted ? 'deleted' : 'released'}`}>{esimData?.isDeleted ? 'Deleted' : (esimData?.status || "-")}</span></p>
                  <PR.Button
                    label={isEsimDeleted ? "Undelete eSIM" : "Delete eSIM"}
                    icon={isEsimDeleted ? "pi pi-replay" : "pi pi-trash"}
                    className={`esim-lifecycle-button ${isEsimDeleted ? "restore" : "delete"}`}
                    onClick={() => setEsimActionDialog(true)}
                    disabled={!esimUserId}
                    tooltip={!esimUserId ? "Customer association is unavailable for this eSIM." : undefined}
                    tooltipOptions={{ position: "top" }}
                  />
                </div>
              </div>

              <div className="esim-details-accordion">
                <div className="flex align-items-center gap-2 w-full mb-3 justify-content-between flex_mobile">
                  <div className="flex align-items-center gap-2">
                    <PR.Image src={esimIcon} alt="Logo" />
                    <span className="font-bold white-space-nowrap">eSIM details</span>
                  </div>

                  <div className="flex align-items-center gap-2">
                    <PR.Tooltip
                      target=".refresh-button"
                      content={
                        isResetCapable
                          ? "Refresh eSIM will instruct the roaming network to disconnect the eSIM and re-register. This will often resolve issues your customers are experiencing."
                          : "Resetting network connection is not supported for this eSIM."
                      }
                      position="top"
                      className="custom-tooltip"
                    />
                    <PR.Button
                      label="Refresh SIM"
                      icon="pi pi-refresh"
                      className="refresh-button exportBtn"
                      onClick={handleResetEsim}
                      loading={resetLoading}
                      disabled={!isResetCapable || resetLoading || capabilitiesLoading || createLoading}
                    />




                  </div>
                </div>
                <div className="details-form">
                  <div className="top-block">
                    <div>
                      <span>PIN</span> <strong>{esimData?.pin || "-"}</strong>
                    </div>
                    <div>
                      <span>PUK</span> <strong>{esimData?.puk || "-"}</strong>
                    </div>
                    <div>
                      <span>Profile</span> <strong>{esimData?.status || "-"}</strong>
                    </div>
                    <div>
                      <span> First Install Date</span> <strong>{(!esimData?.firstInstallDate || esimData?.firstInstallDate?.startsWith("1970-01-01") || esimData?.firstInstallDate?.startsWith("0001-01-01")) ? "-" : esimData?.firstInstallDate}</strong>
                    </div>
                    <div>
                      <span>SMDP+ Address</span> <strong>{esimData?.smdpAddress || "-"}</strong>
                    </div>
                    <div>
                      <span>SMDP-ID</span> <strong>{esimData?.matchingId || "-"}</strong>
                    </div>
                    <div>
                      <span>Latest eSIM Status</span> <strong> <i className="pi pi-exclamation-triangle mr-2"></i> {esimData?.esimStatus || "No data available"}</strong>
                    </div>

                  </div>
                  {/* <div className="bottom-block">
                                    <div className="relative">
                                      <label>Send SMS Notification (0/160 Characters)</label>
                                      <PR.InputTextarea placeholder="Enter your message..." className="message-textarea" rows={4} maxLength={160} />
                                      <PR.Button label="Send" className="send-button" />
                                    </div> 
                                </div> */}
                </div>

                <div className="flex align-items-center gap-2 w-full mb-3 mt-5 justify-content-between">
                  <div className="flex align-items-center gap-2">
                    <PR.Image src={bundleIcon} alt="Logo" />
                    <span className="font-bold white-space-nowrap"> Associated Bundles</span>
                  </div>
                  <div className="switch-toggle">
                    <PR.InputSwitch checked={checked} onChange={(e) => setChecked(e.value)} />
                    <span>Expired bundle</span>
                  </div>
                </div>
                <PR.DataTable value={filteredBundles} sortField="createdTs" sortOrder={-1} className="associated-bundles-table" emptyMessage={<span className="text-gray-500">No results found</span>}>
                  <PR.Column field="providerName" header="Provider Name" body={provider} ></PR.Column>
                  <PR.Column field="name" header="Bundle Name"></PR.Column>
                  <PR.Column field="description" header="Plan"></PR.Column>
                  <PR.Column field="bundleUsage" header="Bundle Usage" body={(rowData) => bundleDetails(rowData)} ></PR.Column>
                  <PR.Column field="createdTs" header="Purchased On" body={createdTime} sortable headerClassName="purchase-date-header"></PR.Column>
                  <PR.Column field="bundleEndTime" header="Expiry On" body={(rowData) => expiryOn(rowData)} ></PR.Column>
                  <PR.Column field="totalOrderAmount" header="Price" body={price}></PR.Column>
                </PR.DataTable>
              </div>
            </div>)}

          </div>

          <AdminFooter />
        </div>
        <PR.Dialog
          visible={esimActionDialog}
          dismissableMask={!esimActionLoading}
          draggable={false}
          resizable={false}
          header={isEsimDeleted ? "Undelete eSIM" : "Delete eSIM"}
          onHide={() => !esimActionLoading && setEsimActionDialog(false)}
          className="affiliate-dialog affiliate-assign"
          maskClassName="affiliate-dialog-mask"
        >
          <div>
            <p>
              Are you sure you want to {isEsimDeleted ? "Undelete" : "delete"} eSIM
              <strong> {esimData?.iccid || searchTerm}</strong>?
            </p>

            <div className="buttons-sections flex gap-3 align-items-center justify-content-end">
              <PR.Button label="Cancel"
                outlined disabled={esimActionLoading}
                onClick={() => setEsimActionDialog(false)}
                className="confirm-button reset-btn del-esim cancel-btn" />
              <PR.Button
                label={isEsimDeleted ? "Undelete" : "Delete"}
                severity={isEsimDeleted ? undefined : "danger"}
                loading={esimActionLoading}
                onClick={handleEsimAction}
                className={`confirm-button del-esim ${isEsimDeleted ? "restore-btn" : "delete-btn"}`}
              />
            </div>
          </div>
        </PR.Dialog>
        <PR.Toast ref={toast} position='top-right' />
      </div>
    </>
  );
};

export default EsimDetails;
